// Runs work once the browser has painted, so a screen shows itself before it starts any network.
// A fetch or script started from a first-render effect begins before the first paint, and Lighthouse
// counts it against first paint on a slow connection (v1.0 quality pass, SPEC §12 item 3).

/** Calls `fn` once the next frame has painted and the browser is idle. Returns a cancel function. */
export function afterFirstPaint(fn: () => void): () => void {
  let done = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let idle: number | undefined;
  const run = () => {
    if (done) return;
    done = true;
    clearTimeout(fallback);
    fn();
  };
  const frame = requestAnimationFrame(() => {
    // The paint's own timestamp lands a few ms after the frame callback: wait for idle (Safari has no idle callback: 50 ms)
    if (typeof requestIdleCallback === 'function') idle = requestIdleCallback(run, { timeout: 300 });
    else timer = setTimeout(run, 50);
  });
  // A hidden tab never runs animation frames: start anyway after a second
  const fallback = setTimeout(run, 1000);
  return () => {
    done = true;
    cancelAnimationFrame(frame);
    if (idle !== undefined) cancelIdleCallback(idle);
    clearTimeout(timer);
    clearTimeout(fallback);
  };
}
